import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { ChannelResponse } from '@/modules/channels/types';
import { Icon } from '@/shared/icons';
import { StatusPill, type StatusPillVariant } from '@/shared/components/ui/StatusPill';
import { CardActionsMenu, type CardAction } from '@/shared/components/ui/CardActionsMenu';
import { IntegrationCredentialsModal } from './IntegrationCredentialsModal';
import { getChannelTypeMetadata } from '../utils/channelTypeMetadata';

export interface EmailAccountListItemProps {
  channel: ChannelResponse;
  members?: Array<{ id: string; name: string; avatar?: string }>;
  onDelete?: (id: string) => void;
  onToggleStatus?: (id: string, currentStatus: string) => void;
  onReconnect?: (id: string) => void;
  /** Seleção (bulk actions) — quando definido, exibe checkbox na primeira coluna */
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
}

/**
 * Linha da lista de "Contas de email" na página de canais — `<tr>/<td>` real (F45),
 * renderizada dentro de `<table><tbody>` pela página.
 *
 * Visual: ícone amber + endereço/meta + contador de threads + status pill + actions menu.
 * Reusa StatusPill e CardActionsMenu (mesmas actions do ChannelCard).
 */
export const EmailAccountListItem: React.FC<EmailAccountListItemProps> = ({
  channel,
  members = [],
  onDelete,
  onReconnect,
  selected = false,
  onToggleSelect,
}) => {
  const navigate = useNavigate();
  const [showIntegrationModal, setShowIntegrationModal] = React.useState(false);

  // Status (mesma lógica do ChannelCard) — StatusPill (F62): verde=ligado, âmbar=aguardando, neutro=inativo
  const statusVariant: StatusPillVariant =
    channel.status === 'active' ? 'success' : channel.status === 'pending' ? 'warning' : 'neutral';
  const statusLabel =
    channel.status === 'active' ? 'Ativo' : channel.status === 'pending' ? 'Aguardando' : 'Inativo';

  // Metadata: getChannelTypeMetadata já retorna `defaultFooter` apropriado pra cada email provider
  const meta = getChannelTypeMetadata(channel.providerId);
  const providerLabel = meta.defaultFooter; // "SendGrid", "Gmail", "AWS SES", "Email"

  // Threads: stats.conversations existe pra todos os tipos (relabel client-side)
  const threadsCount = channel.stats?.conversations ?? 0;

  // Operadores
  const operatorsCount = members.length;

  // Endereço de email
  const emailAddress = channel.identifier || channel.name;

  // Actions (mesmas do ChannelCard)
  const actions: CardAction[] = [
    {
      label: 'Configurações',
      icon: 'settings',
      onClick: () => navigate(`/settings/channels/${channel.id}/edit`),
    },
  ];
  if (channel.integration?.instanceKey) {
    actions.push({
      label: 'Integração',
      icon: 'key',
      onClick: () => setShowIntegrationModal(true),
    });
  }
  if (onReconnect) {
    actions.push({
      label: 'Reconectar',
      icon: 'refresh',
      onClick: () => onReconnect(channel.id),
    });
  }
  if (onDelete) {
    actions.push({
      label: 'Excluir',
      icon: 'trash',
      onClick: () => onDelete(channel.id),
      variant: 'danger',
    });
  }

  return (
    <>
      <tr
        className="border-b transition-colors"
        style={{
          borderColor: 'var(--theme-card-border)',
          backgroundColor: selected ? 'var(--theme-primary-background)' : undefined,
        }}
        onMouseEnter={(e) => {
          if (!selected) e.currentTarget.style.backgroundColor = 'rgba(245, 158, 11, 0.04)';
        }}
        onMouseLeave={(e) => {
          if (!selected) e.currentTarget.style.backgroundColor = 'transparent';
        }}
        data-testid={`email-account-${channel.id}`}
      >
        {/* Checkbox de seleção */}
        {onToggleSelect && (
          <td className="pl-4 pr-1 py-3 w-11">
            <button
              type="button"
              onClick={() => onToggleSelect(channel.id)}
              className="w-[18px] h-[18px] rounded-[5px] border-[1.6px] flex items-center justify-center transition-colors"
              style={{
                backgroundColor: selected ? 'var(--theme-primary)' : 'transparent',
                borderColor: selected ? 'var(--theme-primary)' : 'var(--theme-input-border)',
                color: '#ffffff',
              }}
              aria-label={selected ? 'Desmarcar conta' : 'Selecionar conta'}
              data-testid={`email-account-check-${channel.id}`}
            >
              {selected && <Icon name="check" className="w-3 h-3" />}
            </button>
          </td>
        )}

        {/* Endereço: ícone + endereço + meta */}
        <td className="px-4 py-3 w-full">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="flex items-center justify-center rounded-lg flex-shrink-0"
              style={{
                width: 38,
                height: 38,
                backgroundColor: '#fef3c7',
                color: '#92400e',
              }}
            >
              <Icon name="mail" className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div
                className="font-semibold truncate"
                style={{ fontSize: 13, color: 'var(--theme-card-text)' }}
                data-testid={`email-account-address-${channel.id}`}
              >
                {emailAddress}
              </div>
              <div
                className="truncate mt-0.5"
                style={{ fontSize: 11, color: 'var(--theme-muted-text)' }}
              >
                {providerLabel} · {operatorsCount} {operatorsCount === 1 ? 'operador' : 'operadores'}
              </div>
            </div>
          </div>
        </td>

        {/* Threads abertas */}
        <td className="px-4 py-3 text-right whitespace-nowrap" style={{ fontSize: 11, color: 'var(--theme-muted-text)' }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: 'var(--theme-card-text)',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {threadsCount}
          </div>
          <div>threads abertas</div>
        </td>

        {/* Status pill */}
        <td className="px-4 py-3 whitespace-nowrap">
          <StatusPill variant={statusVariant} label={statusLabel} data-testid={`email-account-status-${channel.id}`} />
        </td>

        {/* Actions menu */}
        <td className="px-4 py-3 text-right w-14">
          <CardActionsMenu actions={actions} size="sm" data-testid={`email-account-actions-${channel.id}`} />
        </td>
      </tr>

      <IntegrationCredentialsModal
        isOpen={showIntegrationModal}
        onClose={() => setShowIntegrationModal(false)}
        instanceKey={channel.integration?.instanceKey || null}
        instanceToken={channel.integration?.instanceToken || null}
        channelName={channel.name}
      />
    </>
  );
};

EmailAccountListItem.displayName = 'EmailAccountListItem';
